import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Icon } from "../../components/Icon";
import { ProgressBar } from "../../components/ui";
import { haptic } from "../../telegram/haptics";
import { micro } from "../../motion";
import { MediaImage } from "../../components/MediaImage";
import { CachedMedia } from "./CachedMedia";
import { formatBytes } from "../../i18n/format";
import { useT, tr } from "../../i18n/strings";
import { isAllowedFor, limitBucketFor } from "../../api/upload";
import type { Attachment, AttachmentKind } from "../../api/types";
import type { Me } from "../../api/types";
import s from "./media.module.css";

export interface StagedFile {
  key: string;
  kind: AttachmentKind;
  file: File;
  /** Object URL for an immediate preview; revoked when the entry goes away. */
  previewUrl: string | null;
}

/**
 * The file the user just picked, straight off the device.
 *
 * A clip gets a `<video>` rather than an `<img>`: the browser paints its own
 * first frame, so the preview is there the instant the file is chosen, with no
 * canvas and nothing to wait for. The poster that actually gets *uploaded* is a
 * separate, better-chosen frame — see `features/media/derivatives`.
 */
function StagedPreview({
  item,
  className,
  fallback = null,
}: {
  item: StagedFile;
  className?: string;
  fallback?: ReactNode;
}) {
  if (!item.previewUrl) return <>{fallback}</>;
  return item.file.type.startsWith("video/") ? (
    <video className={className} src={item.previewUrl} muted playsInline preload="metadata" />
  ) : (
    <MediaImage eager className={className} src={item.previewUrl} />
  );
}

export function stageFile(kind: AttachmentKind, file: File): StagedFile {
  return {
    key: `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2, 8)}`,
    kind,
    file,
    // A picture and a clip both preview straight off the device; a document has
    // nothing to show, so it keeps its glyph.
    previewUrl: /^(image|video)\//.test(file.type) ? URL.createObjectURL(file) : null,
  };
}

/** Reject before spending a round trip; the server would reject the same way. */
function validate(
  kind: AttachmentKind,
  file: File,
  limits: Me["limits"] | undefined,
): string | null {
  // Runs on pick, not on render: reading the language now is correct.
  const t = tr();
  if (!isAllowedFor(kind, file)) return t.media.wrongKind;
  const max = limits?.upload_max_bytes[limitBucketFor(file)];
  if (max && file.size > max) return t.media.tooLarge(Math.round(max / (1024 * 1024)));
  return null;
}

interface SharedProps {
  kind: AttachmentKind;
  existing: Attachment[];
  staged: StagedFile[];
  limits: Me["limits"] | undefined;
  /** null while the artwork does not exist yet — uploads wait for its id. */
  artworkId: string | null;
  progress: Record<string, number>;
  onStage: (files: StagedFile[]) => void;
  onUnstage: (key: string) => void;
  onDeleteExisting: (id: string) => void;
}

/* --------------------------------------------------------------- media -- */

interface Tile {
  /** A staged file's local key, or a saved attachment's id — whichever this is. */
  key: string;
  saved: Attachment | null;
  pending: StagedFile | null;
}

/**
 * The media section: the images and clips a record is catalogued with, one of
 * them marked as the thumbnail the list rows and the detail hero use.
 *
 * The mark is a *selection*, not a kind. Everything stages as `gallery` and the
 * form settles the thumbnail with one `make-primary` call after the uploads —
 * so adding a second photograph never asks the server to delete the first.
 */
export function MediaSlot(
  props: Omit<SharedProps, "kind"> & {
    /** Staged key or attachment id of the thumbnail; null until something is picked. */
    primaryKey: string | null;
    onPickPrimary: (key: string) => void;
  },
) {
  const t = useT();
  const {
    existing,
    staged,
    artworkId,
    progress,
    onStage,
    onUnstage,
    onDeleteExisting,
    limits,
    primaryKey,
    onPickPrimary,
  } = props;
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const max = limits?.media_max_per_artwork ?? 0;

  const tiles: Tile[] = [
    ...existing.map((file) => ({ key: file.id, saved: file, pending: null })),
    ...staged.map((item) => ({ key: item.key, saved: null, pending: item })),
  ];
  // Until /me lands there is no cap to apply — the server still has one. This is
  // the same shape as the size check: no limit known, no local rejection.
  const room = max ? max - tiles.length : Infinity;

  const pick = (files: FileList | null) => {
    if (!files?.length) return;
    const chosen = Array.from(files);
    const accepted: StagedFile[] = [];
    // Over the cap is one message about the section, not one per file past it.
    let problem = chosen.length > room ? t.media.tooMany(max) : null;
    for (const file of chosen.slice(0, Math.max(room, 0))) {
      const issue = validate("gallery", file, limits);
      if (issue) problem = issue;
      else accepted.push(stageFile("gallery", file));
    }
    setError(problem);
    if (problem) haptic.error();
    if (accepted.length) {
      haptic.tap();
      onStage(accepted);
      // The first thing a record gets is its thumbnail until told otherwise.
      if (!primaryKey) onPickPrimary(accepted[0]!.key);
    }
  };

  return (
    <div className={s.slot}>
      <div className={s.grid}>
        <AnimatePresence initial={false}>
          {tiles.map((tile) => {
            const isPrimary = tile.key === primaryKey;
            const isVideo =
              tile.pending?.file.type.startsWith("video/") ??
              tile.saved?.mime_type.startsWith("video/") ??
              false;
            const uploading = progress[tile.key];

            return (
              <motion.div
                key={tile.key}
                layout
                className={[s.tile, isPrimary && s.tilePrimary].filter(Boolean).join(" ")}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={micro}
              >
                {tile.pending ? (
                  <StagedPreview item={tile.pending} className={s.tilePreview} />
                ) : (
                  <CachedMedia
                    media={tile.saved}
                    prefer="thumbnail"
                    className={s.tilePreview}
                    fallback={<Icon name="image" size={20} />}
                  />
                )}

                {isVideo && (
                  <span className={s.tileMark} aria-hidden>
                    <Icon name="video" size={20} />
                  </span>
                )}

                <button
                  type="button"
                  className={`${s.tileRemove} ${s.overlayButton} chrome-media`}
                  aria-label={t.actions.remove}
                  onClick={() => {
                    haptic.tap();
                    if (tile.pending) onUnstage(tile.key);
                    else onDeleteExisting(tile.key);
                  }}
                >
                  <Icon name={tile.pending ? "close" : "delete"} size={20} />
                </button>

                <button
                  type="button"
                  className={[s.tileBadge, isPrimary && s.tileBadgeOn, "chrome-media"]
                    .filter(Boolean)
                    .join(" ")}
                  aria-pressed={isPrimary}
                  aria-label={isPrimary ? t.media.primary : t.actions.makePrimary}
                  onClick={() => {
                    if (isPrimary) return;
                    haptic.tap();
                    onPickPrimary(tile.key);
                  }}
                >
                  <Icon name="thumbnail" size={20} />
                  {isPrimary && <span className={s.tileBadgeLabel}>{t.media.primary}</span>}
                </button>

                {uploading !== undefined && (
                  <div className={s.tileBar}>
                    <ProgressBar value={uploading} />
                  </div>
                )}
              </motion.div>
            );
          })}

          {room > 0 && (
            <motion.button
              key="add"
              layout
              type="button"
              className={`${s.tile} ${s.tileAdd}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={micro}
              onClick={() => input.current?.click()}
            >
              <Icon name="image" size={24} />
              <span>{t.media.pickImageOrVideo}</span>
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      <input
        ref={input}
        type="file"
        multiple
        className={s.hidden}
        accept="image/*,video/*"
        onChange={(event) => {
          pick(event.target.files);
          event.target.value = "";
        }}
      />

      {error && (
        <p className={s.error}>
          <Icon name="alert" size={16} />
          {error}
        </p>
      )}
      {staged.length > 0 && !artworkId && <p className={s.hint}>{t.media.afterSave}</p>}
    </div>
  );
}

/* --------------------------------------------------------- file lists -- */

export function FileSlot(props: SharedProps & { addLabel: string }) {
  const t = useT();
  const {
    kind,
    existing,
    staged,
    artworkId,
    progress,
    onStage,
    onUnstage,
    onDeleteExisting,
    limits,
    addLabel,
  } = props;
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const pick = (files: FileList | null) => {
    if (!files?.length) return;
    const accepted: StagedFile[] = [];
    let problem: string | null = null;
    for (const file of Array.from(files)) {
      const issue = validate(kind, file, limits);
      if (issue) problem = issue;
      else accepted.push(stageFile(kind, file));
    }
    setError(problem);
    if (problem) haptic.error();
    if (accepted.length) {
      haptic.tap();
      onStage(accepted);
    }
  };

  return (
    <div className={s.slot}>
      <AnimatePresence initial={false}>
        {existing.map((file) => (
          <motion.div
            key={file.id}
            layout
            className={`${s.fileRow} paper`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={micro}
          >
            <span className={s.fileMark}>
              <CachedMedia
                media={file}
                prefer="thumbnail"
                className={s.filePreview}
                fallback={<Icon name="file" size={20} />}
              />
            </span>
            <span className={s.fileBody}>
              <span className={`${s.fileName} bidi`}>
                {file.original_filename || file.mime_type}
              </span>
              <span className={s.fileMeta}>{formatBytes(file.size_bytes)}</span>
            </span>
            <button
              type="button"
              aria-label={t.actions.remove}
              onClick={() => {
                haptic.tap();
                onDeleteExisting(file.id);
              }}
            >
              <Icon name="delete" size={20} />
            </button>
          </motion.div>
        ))}

        {staged.map((item) => (
          <motion.div
            key={item.key}
            layout
            className={`${s.fileRow} paper`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={micro}
          >
            <span className={s.fileMark}>
              <StagedPreview
                item={item}
                className={s.filePreview}
                fallback={<Icon name="file" size={20} />}
              />
            </span>
            <span className={s.fileBody}>
              <span className={`${s.fileName} bidi`}>{item.file.name}</span>
              {progress[item.key] !== undefined ? (
                <ProgressBar value={progress[item.key]!} />
              ) : (
                <span className={s.fileMeta}>
                  {formatBytes(item.file.size)}
                  {!artworkId ? ` · ${t.media.afterSave}` : ""}
                </span>
              )}
            </span>
            <button
              type="button"
              aria-label={t.actions.remove}
              onClick={() => {
                haptic.tap();
                onUnstage(item.key);
              }}
            >
              <Icon name="close" size={20} />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>

      <button type="button" className={s.addFile} onClick={() => input.current?.click()}>
        <Icon name="attach" size={20} />
        {addLabel}
      </button>

      <input
        ref={input}
        type="file"
        multiple
        className={s.hidden}
        onChange={(event) => {
          pick(event.target.files);
          event.target.value = "";
        }}
      />

      {error && (
        <p className={s.error}>
          <Icon name="alert" size={16} />
          {error}
        </p>
      )}
    </div>
  );
}

/** Object URLs leak until revoked, and a form can stage many files. */
export function useRevokeOnUnmount(staged: StagedFile[]) {
  const urls = useMemo(() => staged.map((item) => item.previewUrl), [staged]);
  const latest = useRef(urls);
  latest.current = urls;
  useEffect(
    () => () => {
      for (const url of latest.current) if (url) URL.revokeObjectURL(url);
    },
    [],
  );
}
